import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Select, SelectItem } from '@heroui/react'
import { EngineAiOutput, VersionPartiesObject } from '@interfaces/ai'
import { docs } from '@/services/docs/docs-service'
import { Button } from '../ui/button'
import toast from 'react-hot-toast'

interface RiskAnalysisStreamingStatusProps {
  eventsArray: [string, any][]
  publicId: string
  onPartySelection: ({ performFullReview }: { performFullReview: boolean }) => void
}

export interface RiskAnalysisStreamingStatusRef {
  reset: () => void
}

const RiskAnalysisStreamingStatus = forwardRef<RiskAnalysisStreamingStatusRef, RiskAnalysisStreamingStatusProps>(
  ({ eventsArray, publicId, onPartySelection }, ref) => {
    const [message, setMessage] = useState('')
    const [progressPercentage, setProgressPercentage] = useState(0)
    const [isCompleted, setIsCompleted] = useState(false)
    const [parties, setParties] = useState<string[]>([])
    const [selectedParty, setSelectedParty] = useState<string>('')
    const [isUpdating, setIsUpdating] = useState(false)
    const [flipSides, setFlipSides] = useState(false)
    const [partyPromptCopy, setPartyPromptCopy] = useState({
      hasPartiesChanged: false,
      title: '',
      description: '',
      skipButtonText: '',
      selectButtonText: '',
      theme: 'none',
    })

    const lastProcessedIndex = useRef(-1)
    const totalBlocks = useRef(0) // blocks we sent to the AI to review
    const progressPerBlockIncrement = useRef(0) // how much to increment per block

    const handleSwitchSides = () => {
      setFlipSides(false) // hide the button

      setPartyPromptCopy({
        hasPartiesChanged: true,
        title: 'Review from the other side',
        description: 'Which role would you like to review this document from?',
        skipButtonText: '',
        selectButtonText: 'Select role',
        theme: 'info',
      })
    }

    const handlePartySelection = ({ skipBiasedReview }: { skipBiasedReview: boolean }) => {
      let updatedParties: Partial<VersionPartiesObject> = {}
      let performFullReview = false

      // if parties have not changed => party detection is skip-able
      // if parties have changed => party detection can't be skipped, it's either you change your party or you stick to old one
      if (partyPromptCopy.hasPartiesChanged && skipBiasedReview) {
        onPartySelection({ performFullReview })
        return
      }

      setIsUpdating(true)

      if (partyPromptCopy.hasPartiesChanged) {
        updatedParties = {
          userSelectedParty: selectedParty,
          skippedBiasedReview: false,
        }

        performFullReview = true
      } else {
        updatedParties = {
          userSelectedParty: skipBiasedReview ? null : selectedParty,
          skippedBiasedReview: skipBiasedReview,
        }
      }

      docs
        .updateVersionDetails(publicId, { parties: updatedParties })
        .then(() => {
          onPartySelection({ performFullReview })
        })
        .catch(error => {
          toast.error(error.response?.data?.error ?? 'Something went wrong while updating the roles. Please try again.')
        })
        .finally(() => {
          setIsUpdating(false)
        })
    }

    // Expose reset function to parent via ref
    useImperativeHandle(ref, () => ({
      reset: () => {
        setMessage('')
        setIsCompleted(false)
        setParties([])
        setSelectedParty('')
        setIsUpdating(false)
        setFlipSides(false)
        setPartyPromptCopy({
          hasPartiesChanged: false,
          title: '',
          description: '',
          skipButtonText: '',
          selectButtonText: '',
          theme: 'none',
        })
        lastProcessedIndex.current = -1
        progressPerBlockIncrement.current = 0
        totalBlocks.current = 0
      },
    }))

    // Process each event one by one, and handles multiple events being appended to the array
    useEffect(() => {
      // helper function to increase progress
      const increaseProgress = (amount: number) => {
        setProgressPercentage(prev => Math.min(prev + amount, 99)) // 100 is reserved only for completed = true
      }

      // Process only new events (from lastProcessedIndex + 1 to current length)
      for (let i = lastProcessedIndex.current + 1; i < eventsArray.length; i++) {
        const [eventName, payload] = eventsArray[i]

        if (eventName === 'saving_document') {
          setMessage('Saving your latest changes...')
          increaseProgress(3)
        } else if (eventName === 'connecting_to_engine') {
          setMessage('Getting everything ready...')
          setFlipSides(false)

          increaseProgress(3)
        } else if (eventName === 'stream_start') {
          setMessage('Analyzing your document...')

          increaseProgress(3)
        } else if (eventName === 'parsing_end') {
          const data = payload as { eligibleBlocks: number }
          totalBlocks.current = data.eligibleBlocks || 0
          setMessage('Preparing your review...')

          increaseProgress(3)
        } else if (eventName === 'party_detection_end') {
          const data = payload as VersionPartiesObject

          setMessage('Preparing your review...')
          setParties(data.detectedParties || [])
          if (data.tier === 2) setSelectedParty(data.userSelectedParty || '')

          if (data.shouldShowPartyDetectionPrompt) {
            setPartyPromptCopy({
              hasPartiesChanged: false,
              title: 'Who are you in this document?',
              description:
                'Select your role so we can review the document from your perspective. Or skip to continue with a neutral review.',
              skipButtonText: 'Skip sidecheck',
              selectButtonText: 'Select role',
              theme: 'info',
            })
          }

          if (data.hasPartiesChanged) {
            setSelectedParty(data.userSelectedParty || '')

            setPartyPromptCopy({
              hasPartiesChanged: true,
              title: 'We detected different roles.',
              description: 'Which one are you?',
              skipButtonText: 'Do not change roles',
              selectButtonText: 'Select role',
              theme: 'warning',
            })
          }

          increaseProgress(3)
        } else if (eventName === 'preprocessing_document_end') {
          setMessage('Generating your AI review...')

          increaseProgress(5)
          setProgressPercentage(prev => {
            // Calculate how much to increment per block
            const remainingProgress = 100 - prev
            progressPerBlockIncrement.current = totalBlocks.current > 0 ? remainingProgress / totalBlocks.current : 0
            return prev
          })
        } else if (eventName === 'block_analyzed') {
          increaseProgress(progressPerBlockIncrement.current)
        } else if (eventName === 'stream_end') {
          const data = payload as { versionParties?: VersionPartiesObject; aiOutputFrontendExpected?: EngineAiOutput }

          setMessage('')

          if (!data.aiOutputFrontendExpected) return // stream ended halfway

          setIsCompleted(true)

          // this guy does/should not get reset
          setProgressPercentage(0) // to prepare for the next round of review

          if (data.versionParties) {
            const { tier, userSelectedParty, detectedParties } = data.versionParties

            setParties(detectedParties || [])
            if (tier === 2) setSelectedParty(userSelectedParty || '')
            if (tier === 1 || tier === 2) setFlipSides(true)
          }
        }
      }

      // Mark all events as processed
      lastProcessedIndex.current = eventsArray.length - 1
    }, [eventsArray.length])

    // Return null when empty (effect still runs for reset, but nothing renders)
    if (eventsArray.length === 0 || eventsArray[0][0] === 'stream_end') return null

    return (
      <Card className="mt-4 p-4">
        <div>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isCompleted ? 'Review completed!' : 'Review in progress...'}
            </h3>
            <span title={`${progressPercentage}%`} className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {Math.round(isCompleted ? 100 : progressPercentage)}%
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-2 rounded-full bg-green-500 transition-all duration-300 ease-out"
              style={{ width: `${isCompleted ? 100 : progressPercentage}%` }}
            />
          </div>
        </div>

        {message && (
          <div className="mt-4">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mt-2 h-2 w-2 flex-shrink-0 animate-ping rounded-full bg-green-500"></div>
              <div className="min-w-0 flex-1">
                <div
                  dangerouslySetInnerHTML={{ __html: message }}
                  className={cn('break-words text-sm font-medium text-gray-900 dark:text-gray-100')}
                />
              </div>
            </div>
          </div>
        )}

        {partyPromptCopy.theme !== 'none' && (
          <div
            className={cn(
              'mt-4 rounded-lg border p-4',
              partyPromptCopy.theme === 'info' ? 'border-blue-200 bg-blue-50' : 'border-amber-300 bg-amber-50'
            )}
          >
            <h4
              className={cn(
                'mb-2 flex items-center gap-2 text-sm font-semibold',
                partyPromptCopy.theme === 'info' ? 'text-blue-900' : 'text-amber-900'
              )}
            >
              {partyPromptCopy.theme === 'info' ? (
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {partyPromptCopy.title}
            </h4>
            <p className={cn('mb-3 text-sm', partyPromptCopy.theme === 'info' ? 'text-blue-800' : 'text-amber-800')}>
              {partyPromptCopy.description}
            </p>

            <div className="mb-3">
              <Select
                label={'Select a role'}
                placeholder="Click to see the roles..."
                size="sm"
                selectedKeys={selectedParty ? [selectedParty] : []}
                onSelectionChange={keys => {
                  const selected = Array.from(keys)[0] as string
                  setSelectedParty(selected)
                }}
                classNames={{
                  label: cn(
                    'text-xs font-medium',
                    partyPromptCopy.theme === 'info' ? 'text-blue-900' : 'text-amber-900'
                  ),
                  trigger: partyPromptCopy.theme === 'info' ? 'border-blue-300' : 'border-amber-300',
                }}
              >
                {parties.map(party => (
                  <SelectItem key={party}>{party}</SelectItem>
                ))}
              </Select>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handlePartySelection({ skipBiasedReview: false })}
                disabled={!selectedParty || isUpdating}
                className={cn(
                  'inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                  partyPromptCopy.theme === 'info'
                    ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    : 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
                )}
              >
                {isUpdating ? 'Saving...' : partyPromptCopy.selectButtonText}
              </button>
              <button
                onClick={() => handlePartySelection({ skipBiasedReview: true })}
                disabled={isUpdating}
                className={cn(
                  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                  partyPromptCopy.theme === 'info' ? 'focus:ring-blue-500' : 'focus:ring-amber-500',
                  !partyPromptCopy.skipButtonText && 'hidden'
                )}
              >
                {isUpdating ? 'Saving...' : partyPromptCopy.skipButtonText}
              </button>
            </div>
          </div>
        )}

        {flipSides && (
          <div className="mt-4 flex items-center justify-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <span className="text-sm">Review from the other side?</span>
            <Button size={'sm'} variant={'accentGreen'} onClick={() => handleSwitchSides()}>
              Switch role
            </Button>
          </div>
        )}
      </Card>
    )
  }
)

export default RiskAnalysisStreamingStatus
